/**
 * O QUE O APARELHO LEMBRA DA ENTRADA, e onde a senha fica (pedido do dono,
 * 31/10/2026: "todas as vezes que eu faço logout some o e-mail e a senha; quero,
 * como a maioria dos sites, um armazenamento seguro desses dados, se o usuário
 * quiser").
 *
 * ## A senha NUNCA passa por aqui
 *
 * O lugar seguro de uma senha é o gerenciador de senhas do navegador ou do
 * sistema (Chrome, Edge, Android, Safari e as chaves do iCloud): ele cifra,
 * sincroniza e pede a digital antes de preencher. O `localStorage` é texto
 * aberto para qualquer script da página. Por isso este arquivo guarda só:
 *
 *  - a escolha "Lembrar meus dados neste aparelho" (ligada por padrão);
 *  - a escolha "Manter conectado" (ligada por padrão);
 *  - o ÚLTIMO E-MAIL que entrou, e só com a primeira escolha ligada.
 *
 * A senha vai ao gerenciador do navegador pela API de credenciais
 * (`navigator.credentials.store`, onde existe: Chrome, Edge, Android), e só com
 * "Lembrar meus dados" ligado. Onde a API não existe (Safari, Firefox), quem
 * oferece guardar é o próprio navegador, ao ver o formulário de entrada
 * (`name`, `autocomplete` e o envio do `<form>`, nas duas telas).
 *
 * ## Sair
 *
 * `preventSilentAccess()` sempre (é a prática da especificação: depois de sair,
 * o navegador não entrega a senha sem a pessoa escolher). O e-mail fica se a
 * escolha estiver ligada; desligada, sai junto.
 *
 * Tudo aqui engole a falha: aparelho sem armazenamento (aba privada do Safari)
 * ou sem a API só deixa de lembrar, nunca impede de entrar.
 */

const CHAVE_DO_LEMBRAR = 'ct-frotas:entrada:lembrar';
const CHAVE_DO_MANTER = 'ct-frotas:entrada:manter-conectado';
const CHAVE_DO_EMAIL = 'ct-frotas:entrada:email';

/** As escolhas da entrada neste aparelho, e o e-mail que ela lembra. */
export interface EscolhasDaEntrada {
  /** "Lembrar meus dados neste aparelho". */
  readonly lembrar: boolean;
  /** "Manter conectado": a sessão vale até a pessoa sair (0135). */
  readonly manter: boolean;
  /** O último e-mail, ou vazio quando não há ou "Lembrar" está desligado. */
  readonly email: string;
}

function deposito(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function ler(chave: string): string | null {
  try {
    return deposito()?.getItem(chave) ?? null;
  } catch {
    return null;
  }
}

function gravar(chave: string, valor: string | null): void {
  try {
    const d = deposito();
    if (!d) return;
    if (valor === null) d.removeItem(chave);
    else d.setItem(chave, valor);
  } catch {
    /* Sem armazenamento: o aparelho só deixa de lembrar. */
  }
}

/** O que a tela de entrada abre preenchido. As duas escolhas nascem ligadas. */
export function lerEscolhasDaEntrada(): EscolhasDaEntrada {
  const lembrar = ler(CHAVE_DO_LEMBRAR) !== '0';
  const manter = ler(CHAVE_DO_MANTER) !== '0';
  return { lembrar, manter, email: lembrar ? (ler(CHAVE_DO_EMAIL) ?? '') : '' };
}

/**
 * Depois de ENTRAR: guarda as duas escolhas e, com "Lembrar" ligado, o e-mail.
 * Desligado, o e-mail que houver sai — a pessoa acabou de dizer que este
 * aparelho não é para lembrar.
 */
export function guardarEscolhasDaEntrada(escolhas: EscolhasDaEntrada): void {
  gravar(CHAVE_DO_LEMBRAR, escolhas.lembrar ? '1' : '0');
  gravar(CHAVE_DO_MANTER, escolhas.manter ? '1' : '0');
  const email = escolhas.email.trim();
  gravar(CHAVE_DO_EMAIL, escolhas.lembrar && email !== '' ? email : null);
}

/* ------------------------------------------- o gerenciador de senhas do navegador */

/** O pedaço da API de credenciais que o TypeScript não descreve (`PasswordCredential`). */
interface CredencialDeSenha {
  readonly id: string;
  readonly password?: string;
  readonly type?: string;
}
type ConstrutorDeCredencial = new (dados: { id: string; password: string; name?: string }) => CredencialDeSenha;

interface ApiDeCredenciais {
  store?: (credencial: unknown) => Promise<unknown>;
  get?: (opcoes: unknown) => Promise<unknown>;
  preventSilentAccess?: () => Promise<void>;
}

function apiDeCredenciais(): ApiDeCredenciais | null {
  try {
    const nav = (globalThis as { navigator?: { credentials?: ApiDeCredenciais } }).navigator;
    return nav?.credentials ?? null;
  } catch {
    return null;
  }
}

function construtorDaCredencial(): ConstrutorDeCredencial | null {
  const c = (globalThis as { PasswordCredential?: unknown }).PasswordCredential;
  return typeof c === 'function' ? (c as ConstrutorDeCredencial) : null;
}

/**
 * Entrega e-mail e senha ao gerenciador do navegador, onde a API existe. O
 * navegador pergunta à pessoa antes de guardar; nada fica no sistema. Devolve
 * se o pedido saiu (não se a pessoa aceitou: isso o navegador não conta).
 */
export async function guardarNoNavegador(dados: { readonly email: string; readonly senha: string; readonly nome?: string | null }): Promise<boolean> {
  const api = apiDeCredenciais();
  const Credencial = construtorDaCredencial();
  const email = dados.email.trim();
  if (!api?.store || !Credencial || email === '' || dados.senha === '') return false;
  try {
    const nome = dados.nome?.trim();
    await api.store(new Credencial({ id: email, password: dados.senha, ...(nome ? { name: nome } : {}) }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Pede ao gerenciador do navegador a senha guardada para este endereço, para
 * PREENCHER a tela — nunca para entrar sozinho: o botão continua sendo da
 * pessoa. `mediation: 'optional'`: com uma conta e o acesso liberado, o
 * navegador entrega sem perguntar; depois de sair, ele mostra a escolha.
 */
export async function buscarNoNavegador(): Promise<{ readonly email: string; readonly senha: string } | null> {
  const api = apiDeCredenciais();
  if (!api?.get || !construtorDaCredencial()) return null;
  try {
    const credencial = (await api.get({ password: true, mediation: 'optional' })) as CredencialDeSenha | null;
    if (!credencial || typeof credencial.id !== 'string' || typeof credencial.password !== 'string') return null;
    if (credencial.type !== undefined && credencial.type !== 'password') return null;
    return { email: credencial.id, senha: credencial.password };
  } catch {
    return null;
  }
}

/**
 * Depois de SAIR: o navegador deixa de entregar a senha sem a pessoa escolher
 * (`preventSilentAccess`), e o e-mail só fica se "Lembrar" estiver ligado.
 */
export async function esquecerAoSair(): Promise<void> {
  if (!lerEscolhasDaEntrada().lembrar) gravar(CHAVE_DO_EMAIL, null);
  const api = apiDeCredenciais();
  if (!api?.preventSilentAccess) return;
  try {
    await api.preventSilentAccess();
  } catch {
    /* Sem a API: não há o que travar. */
  }
}

/**
 * Depois de ENTRAR (pela senha, ou pela senha e o código): as escolhas, o
 * e-mail e, com "Lembrar" ligado, a senha entregue ao gerenciador do
 * navegador. As duas telas de entrada (mesa e celular) chamam esta, e só ela,
 * para lembrarem igual.
 */
export async function lembrarDepoisDeEntrar(dados: {
  readonly email: string;
  readonly senha: string;
  readonly lembrar: boolean;
  readonly manter: boolean;
  readonly nome?: string | null;
}): Promise<void> {
  guardarEscolhasDaEntrada({ lembrar: dados.lembrar, manter: dados.manter, email: dados.email });
  if (dados.lembrar) await guardarNoNavegador({ email: dados.email, senha: dados.senha, nome: dados.nome ?? null });
}

/**
 * O que o gerenciador do navegador devolveu vira o conteúdo dos campos SÓ se
 * a pessoa ainda não digitou nada diferente: a senha vazia, e o e-mail vazio
 * ou o mesmo. Devolve `null` quando não há o que preencher. Pura.
 */
export function preencherComOGuardado(
  atual: { readonly email: string; readonly senha: string },
  guardado: { readonly email: string; readonly senha: string } | null,
): { readonly email: string; readonly senha: string } | null {
  if (!guardado || guardado.email.trim() === '' || guardado.senha === '') return null;
  if (atual.senha !== '') return null;
  const digitado = atual.email.trim().toLowerCase();
  if (digitado !== '' && digitado !== guardado.email.trim().toLowerCase()) return null;
  return { email: guardado.email, senha: guardado.senha };
}
